import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  HiOutlineDocument,
  HiOutlineEye,
  HiOutlineArrowDownTray,
} from "react-icons/hi2";

import { FilePreviewDialog } from "./FilePreviewDialog";
import { fileTypeLabel, formatBytes } from "./formatting";
import type { ShellServices } from "./services";

function saveAs(url: string, filename: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/** Файл из внутреннего сообщения с тем же предпросмотром, что на странице файлов. */
export function StoredFileAttachment({
  fileId,
  services,
}: {
  fileId: string;
  services: ShellServices;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const file = useQuery({
    queryKey: ["files", fileId],
    queryFn: () => services.files.get!(fileId),
    enabled: services.files.get !== undefined,
    retry: false,
  });
  const download = useMutation({
    mutationFn: () => services.files.download(fileId),
    onSuccess: ({ data }) =>
      saveAs(data.url, file.data?.data.filename ?? "attachment"),
  });
  const record = file.data?.data;

  return (
    <li className="stored-file">
      <span className="stored-file__icon" aria-hidden="true">
        <HiOutlineDocument />
      </span>
      <span className="stored-file__body">
        <strong>{record?.filename ?? "Вложение"}</strong>
        {record ? (
          <small>
            {fileTypeLabel(record.mime_type)} · {formatBytes(record.size)}
          </small>
        ) : file.isPending ? (
          <small aria-busy="true">Загружаем сведения…</small>
        ) : null}
      </span>
      {record ? (
        <span className="stored-file__actions">
          <button
            type="button"
            className="button button--secondary button--compact"
            aria-label={`Открыть ${record.filename}`}
            onClick={() => setPreviewOpen(true)}
          >
            <HiOutlineEye aria-hidden="true" />
            <span>Открыть</span>
          </button>
          <button
            type="button"
            className="button button--text button--compact"
            aria-label={`Скачать ${record.filename}`}
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            <HiOutlineArrowDownTray aria-hidden="true" />
            <span>{download.isPending ? "Скачиваем…" : "Скачать"}</span>
          </button>
        </span>
      ) : null}
      {file.isError || download.isError ? (
        <small className="stored-file__error" role="alert">
          Файл недоступен. Обновите страницу или повторите попытку позже.
        </small>
      ) : null}
      {previewOpen && record ? (
        <FilePreviewDialog
          file={record}
          services={services}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </li>
  );
}
